import {
  useEffect, useState,
} from 'react';
import { castArray, isEmpty } from 'lodash';
import { Autocomplete, TextField } from '@mui/material';
import { Role } from 'lib/types';
import { User } from 'lib/types/users';
import { useApi } from 'lib/contexts/ApplicationState';
import { InputAdornmentCheck } from 'elements/InputAdornmentCheck';
import { useRoles } from 'lib/contexts/rolesContext';
import { createFilterOptions } from '@mui/material/Autocomplete';
import InfoIcon from '@mui/icons-material/Info';
import { MobileTooltip } from 'components/MobileTooltip';

export type RoleSelectValue = Role | Role[] | null;
export interface Props {
  onChange: (value: RoleSelectValue) => void;
  multiple?: boolean;
  value: RoleSelectValue;
  selectedUser?: User | null;
  label?: string;
  size?: 'small' | 'medium';
  limitTags?: number;
  excludePhasedOut?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  placeholder?: string;
  id?: string;
}

// Include aliases in the string that we're matching.
const filterOptions = createFilterOptions({
  stringify: (option: Role) => `${option.name} ${castArray(option.aliases).join(' ')}`,
});

export const RoleSelect = ({
  label, onChange, value, selectedUser, multiple, className, placeholder,
  size, limitTags, excludePhasedOut = false, disabled = false, autoFocus = false, id,
}: Props) => {
  const allRoles = useRoles();
  const API = useApi();
  const [roles, setRoles] = useState<Role[]>();

  useEffect(() => {
    async function fetchSelectedUserRoles() {
      if (!selectedUser?.id) {
        return;
      }

      const userRoles = await API.getUserRoles(selectedUser.id, true);
      if (isEmpty(userRoles)) {
        if (excludePhasedOut) {
          setRoles(allRoles?.filter((r) => r.phasedOut === false));
        } else {
          setRoles(allRoles);
        }
        return;
      }

      // dedup all roles and put the userRoles on top
      const availableRoles = excludePhasedOut ? (
        allRoles?.filter((r) => r.phasedOut === false)
      ) : allRoles;
      setRoles([
        ...userRoles,
        ...(availableRoles || []).filter((r) => !userRoles.find((u) => u.id === r.id)),
      ]);
    }

    // make sure allRoles are populate first
    if (!allRoles) return;

    if (selectedUser?.id) {
      fetchSelectedUserRoles();
    } else {
      setRoles(allRoles);
    }
  }, [selectedUser, allRoles, API, excludePhasedOut]);

  return (
    <div className="relative">
      {disabled
        && (
          <MobileTooltip
            placement="top"
            arrow
            className="absolute -top-2 -left-1 z-20"
            title="This role may not be changed for this company or brand. If you believe this is in error, please let us know."
          >
            <InfoIcon fontSize="inherit" color="secondary" />
          </MobileTooltip>
        )}
      <Autocomplete
        className={className}
        multiple={multiple}
        options={roles || []}
        onChange={(_, v) => onChange(v)}
        value={value}
        getOptionLabel={(option) => option?.name || ''}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, v) => option?.id === v?.id}
        limitTags={limitTags}
        disabled={disabled}
        renderInput={(params) => {
          const newInputProps = !multiple && !isEmpty(value) ? {
            ...params.InputProps,
            startAdornment: <InputAdornmentCheck />,
          } : { ...params.InputProps };
          return (
            <TextField
              {...params}
              autoFocus={autoFocus}
              label={label}
              placeholder={placeholder}
              variant="outlined"
              InputProps={newInputProps}
              {...(size && { size })}
              {...(id && { id })}
            />
          );
        }}
      />
    </div>
  );
};
